import { useState, useEffect } from 'react';
import { CompositeDecorator, EditorState, convertFromRaw } from 'draft-js';
import { contentStateToPageCopyHtml, pageCopyHtmlToContentState } from './pageCopyHtml';

/** `raw` stores Draft.js JSON (the default); `html` stores the page-copy HTML subset (see pageCopyHtml). */
export type RichTextFormat = 'raw' | 'html';

type Decorator = InstanceType<typeof CompositeDecorator>;

export const useConvertEditorState = (value: any, format: RichTextFormat = 'raw', decorator?: Decorator) => {
  const [editorState, setEditorState] = useState(calcState(value, format, decorator));

  useEffect(() => {
    // In html mode the editor reports its own HTML back through `value`; keep the current state (and
    // its selection/undo history) when nothing changed instead of rebuilding it.
    setEditorState((current: any) =>
      format === 'html' && contentStateToPageCopyHtml(current.getCurrentContent()) === (value ?? '')
        ? current
        : calcState(value, format, decorator),
    );
  }, [value, format, decorator]);

  return [editorState, setEditorState];
};

const calcState = (value: any, format: RichTextFormat, decorator?: Decorator) => {
  if (format === 'html') return EditorState.createWithContent(pageCopyHtmlToContentState(value), decorator);
  return value ? EditorState.createWithContent(convertFromRaw(JSON.parse(value))) : EditorState.createEmpty();
};
